import React, { useRef, useLayoutEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Diamond,
  Save,
  Mail,
  Trash2,
  Download,
  FileText,
  Package,
  Truck,
  Zap,
  Hammer,
  Wrench,
  Settings,
  Timer,
  Battery,
  RotateCcw,
} from "lucide-react";
import { LOVABLE_FLOW_SEPARATOR } from "../lovableFlowSeparators";
import { getLovableSwitchPositionClass } from "../../../../CustomHeadlessTable/switchDisplayValue";
import { useTableVariant } from "../../../../CustomHeadlessTable/TableVariantContext";

/** Detect Lovable-themed detail flows (LCA, PCF, company GHG). */
export const useLovableLcaContext = () => {
  const ref = useRef(null);
  const [isLovableLca, setIsLovableLca] = useState(false);

  useLayoutEffect(() => {
    const root = ref.current;
    setIsLovableLca(
      !!root?.closest(".azilis-detail-flows, .company-ghg-yfs")
    );
  }, []);

  return { ref, isLovableLca };
};

/** Wraps Yes/No (or 3-way) switches with selected-segment styling in LCA flows. */
export const LovableSwitchWrap = ({ which, column, children }) => {
  const tableVariant = useTableVariant();
  const { ref, isLovableLca } = useLovableLcaContext();
  const isLovable = tableVariant === "lovable" || isLovableLca;
  if (!isLovable) {
    return children;
  }
  const position = getLovableSwitchPositionClass(which, column);
  return (
    <div ref={ref} className={`lovable-switch-wrap ${position}`}>
      {children}
    </div>
  );
};

/**
 * Lovable table row actions: Diamond (compare), Save, Mail, Trash2.
 */
export const LovableRowActions = ({
  onCompare,
  onSave,
  onMail,
  onDelete,
  showCompare = false,
  isCompareSelected = false,
  compareDisabled = false,
  showSave = true,
  showMail = true,
  showDelete = true,
}) => (
  <div className="lovable-row-actions">
    {showCompare && (
      <button
        type="button"
        className={[
          "lovable-row-action-btn",
          isCompareSelected ? "lovable-row-action-btn--selected" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={onCompare}
        disabled={compareDisabled}
        aria-label={
          isCompareSelected
            ? "Remove from comparison"
            : "Add to comparison (max 5)"
        }
        title={
          isCompareSelected
            ? "Fjern fra sammenligning"
            : "Tilføj til sammenligning (maks 5)"
        }
        aria-pressed={isCompareSelected}
      >
        <Diamond strokeWidth={isCompareSelected ? 2.5 : 2} />
      </button>
    )}
    {showSave && (
      <button
        type="button"
        className="lovable-row-action-btn"
        onClick={onSave}
        aria-label="Save row"
      >
        <Save strokeWidth={2} />
      </button>
    )}
    {showMail && (
      <button
        type="button"
        className="lovable-row-action-btn"
        onClick={onMail}
        aria-label="Send message"
      >
        <Mail strokeWidth={2} />
      </button>
    )}
    {showDelete && (
      <button
        type="button"
        className="lovable-row-action-btn lovable-row-action-btn--danger"
        onClick={onDelete}
        aria-label="Delete row"
      >
        <Trash2 strokeWidth={2} />
      </button>
    )}
  </div>
);

const SEPARATOR_ICON_MAP = {
  [LOVABLE_FLOW_SEPARATOR.RAW_MATERIALS]: Package,
  [LOVABLE_FLOW_SEPARATOR.TRANSPORT]: Truck,
  [LOVABLE_FLOW_SEPARATOR.ENERGY]: Zap,
  [LOVABLE_FLOW_SEPARATOR.MANUFACTURING]: Hammer,
  [LOVABLE_FLOW_SEPARATOR.SETTINGS]: Settings,
  [LOVABLE_FLOW_SEPARATOR.INSTALLATION]: Zap,
  [LOVABLE_FLOW_SEPARATOR.SERVICE_LIFE]: Timer,
  [LOVABLE_FLOW_SEPARATOR.USE]: Battery,
  [LOVABLE_FLOW_SEPARATOR.MAINTENANCE]: Wrench,
  [LOVABLE_FLOW_SEPARATOR.REPAIR]: Wrench,
  [LOVABLE_FLOW_SEPARATOR.REPLACEMENT]: Settings,
  [LOVABLE_FLOW_SEPARATOR.REFURBISHMENT]: RotateCcw,
  [LOVABLE_FLOW_SEPARATOR.END_OF_LIFE]: Trash2,
  [LOVABLE_FLOW_SEPARATOR.DISPOSAL]: Trash2,
};

/** Phase transition icon — Lovable: centered w-16/w-20 gradient circle. */
export const LovableFlowSeparator = ({
  type = LOVABLE_FLOW_SEPARATOR.TRANSPORT,
  variant = "slate",
  size = "md",
  label,
}) => {
  const Icon = SEPARATOR_ICON_MAP[type] || Truck;
  const isZap = type === LOVABLE_FLOW_SEPARATOR.ENERGY || type === LOVABLE_FLOW_SEPARATOR.INSTALLATION;
  const ringVariant = variant === "amber" || isZap ? "amber" : "slate";

  return (
    <div className="lovable-flow-separator" role="presentation">
      <div className="lovable-flow-separator__center">
        <div
          className={[
            "lovable-flow-separator__ring",
            `lovable-flow-separator__ring--${ringVariant}`,
            size === "lg" ? "lovable-flow-separator__ring--lg" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Icon
            className={[
              "lovable-flow-separator__icon",
              isZap ? "lovable-flow-separator__icon--filled" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            strokeWidth={2}
            aria-hidden
          />
        </div>
      </div>
      {label ? <span className="sr-only">{label}</span> : null}
    </div>
  );
};

/** Slate secondary action (export, PDF, etc.) — matches Lovable add-row / delete language. */
export const LovableSecondaryButton = ({
  onClick,
  disabled = false,
  icon: Icon,
  children,
  className = "",
  type = "button",
}) => (
  <button
    type={type}
    className={`lovable-secondary-btn ${className}`.trim()}
    onClick={onClick}
    disabled={disabled}
  >
    {Icon ? (
      <Icon className="lovable-secondary-btn__icon" strokeWidth={2} aria-hidden />
    ) : null}
    {children != null && children !== "" ? <span>{children}</span> : null}
  </button>
);

/** Export to Excel — Lovable secondary with download icon. */
export const LovableExportExcelButton = ({
  onClick,
  disabled = false,
  isExporting = false,
  label = "Export to Excel",
}) => (
  <LovableSecondaryButton
    icon={Download}
    onClick={onClick}
    disabled={disabled || isExporting}
  >
    {isExporting ? "Exporting..." : label}
  </LovableSecondaryButton>
);

/** Generate PDF — Lovable secondary with document icon. */
export const LovableGeneratePdfButton = ({
  onClick,
  disabled = false,
  isGenerating = false,
  label = "Generate PDF",
}) => (
  <LovableSecondaryButton
    icon={FileText}
    onClick={onClick}
    disabled={disabled || isGenerating}
  >
    {isGenerating ? "Generating PDF..." : label}
  </LovableSecondaryButton>
);

/** Delete entire product — slate secondary with danger hover (LCA pages). */
export const LovableDeleteProductButton = ({
  label = "Delete product",
  onClick,
  disabled = false,
}) => (
  <div className="lovable-delete-product">
    <button
      type="button"
      className="lovable-delete-product__btn"
      onClick={onClick}
      disabled={disabled}
    >
      <Trash2 className="lovable-delete-product__icon" strokeWidth={2} aria-hidden />
      <span>{label}</span>
    </button>
  </div>
);

/** Lovable: centered secondary button with Plus only (below table). */
export const LovableAddRowButton = ({ onClick, disabled, className = "" }) => (
  <button
    type="button"
    className={`lovable-add-row-btn ${className}`.trim()}
    onClick={onClick}
    disabled={disabled}
    aria-label="Add row"
  >
    <AddIcon className="lovable-add-row-btn__icon" />
  </button>
);

/**
 * Lovable: ChevronDown — points down when collapsed (hidden), rotates up when open.
 */
export const LovableShowHideIcon = ({
  isOpen,
  onClick,
  size = "md",
  className = "",
  disabled = false,
  ariaLabel,
}) => {
  const sizeClass =
    size === "lg" ? "lovable-show-hide-btn--lg" : "lovable-show-hide-btn--md";

  return (
    <button
      type="button"
      className={[
        "lovable-show-hide-btn",
        sizeClass,
        isOpen ? "lovable-show-hide-btn--open" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || (isOpen ? "Hide section" : "Show section")}
      aria-expanded={isOpen}
    >
      <KeyboardArrowDownIcon className="lovable-show-hide-btn__icon" />
    </button>
  );
};

/** Wrapper that picks Lovable chevron vs legacy img icons. */
export const LovableAwareShowHide = ({
  isOpen,
  onToggle,
  legacyShowSrc,
  legacyHideSrc,
  size = "md",
  imgClassName = "",
  imgStyle,
  disabled = false,
}) => {
  const { ref, isLovableLca } = useLovableLcaContext();

  return (
    <span
      ref={ref}
      className={isLovableLca ? imgClassName : undefined}
      style={
        isLovableLca
          ? {
              display: "inline-flex",
              lineHeight: 0,
              width: "fit-content",
              height: "fit-content",
            }
          : undefined
      }
    >
      {isLovableLca ? (
        <LovableShowHideIcon
          isOpen={isOpen}
          onClick={onToggle}
          size={size}
          disabled={disabled}
        />
      ) : (
      <img
        src={isOpen ? legacyHideSrc : legacyShowSrc}
        alt={isOpen ? "Hide" : "Show"}
        width={size === "lg" ? 40 : 25}
        height={size === "lg" ? 40 : 25}
        onClick={() => !disabled && onToggle()}
        className={imgClassName}
        style={{
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "default" : "pointer",
          ...imgStyle,
        }}
      />
      )}
    </span>
  );
};
